import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { FileText, Plus, Pencil, Trash2, Printer, FileSignature, Package, Upload, Download } from "lucide-react";
import { useAuth } from "@/AuthContext";
import { API } from "@/config";

export const DeliveryFormView = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [delivery, setDelivery] = useState(null);
  const [invoice, setInvoice] = useState(null);
  const [logoUrl, setLogoUrl] = useState(null);

  useEffect(() => {
    fetchDelivery();
    fetchInvoice();
    fetchLogo();
  }, [id]);

  const fetchDelivery = async () => {
    try {
      const response = await axios.get(`${API}/deliveries/invoice/${id}`);
      setDelivery(response.data);
    } catch (error) {
      toast.error("Delivery record not found");
      navigate(`/invoices/${id}`);
    }
  };

  const fetchInvoice = async () => {
    try {
      const response = await axios.get(`${API}/invoices/${id}`);
      setInvoice(response.data);
    } catch (error) {
      console.log("Error fetching invoice");
    }
  };

  const fetchLogo = async () => {
    try {
      const response = await axios.get(`${API}/logo`, { responseType: 'blob' });
      const url = URL.createObjectURL(response.data);
      setLogoUrl(url);
    } catch (error) {
      console.log("No logo uploaded yet");
    }
  };

  const handlePrint = () => {
    window.print();
  };

  if (!delivery || !invoice) {
    return <div>Loading...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between print:hidden">
        <h1 className="text-4xl font-bold" data-testid="delivery-view-title">Delivery Form</h1>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => navigate(`/invoices/${id}`)} data-testid="back-to-invoice-btn">
            Back to Invoice
          </Button>
          <Button onClick={handlePrint} data-testid="print-delivery-btn">
            <Printer className="h-4 w-4 mr-2" />
            Print Delivery Form
          </Button>
        </div>
      </div>

      <Card className="print:shadow-none" data-testid="delivery-card">
        <CardContent className="p-8">
          <div className="space-y-8 invoice-content">
            {/* Header */}
            <div className="text-center border-b-2 pb-6">
              {logoUrl && (
                <img src={logoUrl} alt="Ellantes Logo" className="h-20 mx-auto mb-4" data-testid="delivery-logo" />
              )}
              <h2 className="text-3xl font-bold">DELIVERY RECEIPT</h2>
              <div className="text-lg mt-2">Invoice: {delivery.invoice_number}</div>
            </div>

            {/* Company & Customer Info */}
            <div className="grid grid-cols-2 gap-8">
              <div>
                <h3 className="font-bold text-lg mb-2">From:</h3>
                <div className="space-y-1">
                  <div className="font-semibold">Ellantes LLC</div>
                  <div className="text-sm">1309 Coffeen Avenue</div>
                  <div className="text-sm">Sheridan, WY 82801</div>
                  <div className="text-sm">EIN: 33-3145594</div>
                </div>
              </div>
              <div>
                <h3 className="font-bold text-lg mb-2">Delivered To:</h3>
                <div className="space-y-1">
                  <div className="font-semibold">{delivery.customer_name}</div>
                  <div className="text-sm">{delivery.customer_address}</div>
                </div>
              </div>
            </div>

            {/* Delivery Info */}
            <div className="grid grid-cols-2 gap-8 bg-gray-50 p-4 rounded-lg print-keep-together">
              <div>
                <div className="text-sm font-semibold">Delivery Date:</div>
                <div>{new Date(delivery.delivery_date).toLocaleDateString()}</div>
              </div>
              <div>
                <div className="text-sm font-semibold">Delivered By:</div>
                <div>{delivery.delivered_by}</div>
              </div>
            </div>

            {/* Items with Serial Numbers */}
            <div>
              <h3 className="font-bold text-lg mb-4">Equipment Delivered</h3>
              {delivery.items.map((item, itemIndex) => (
                <Card key={itemIndex} className="mb-4 print-keep-together">
                  <CardHeader className="bg-blue-50">
                    <CardTitle className="text-base flex justify-between">
                      <span>{item.product_name}</span>
                      <span>Quantity: {item.quantity}</span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pt-4">
                    <div className="space-y-2">
                      <div className="font-semibold">Serial Numbers:</div>
                      {item.serial_numbers.map((sn, snIndex) => (
                        <div key={snIndex} className="flex items-center gap-4 p-2 bg-gray-50 rounded">
                          <span className="font-mono text-sm w-8">#{snIndex + 1}</span>
                          <span className="font-mono font-semibold text-lg">{sn}</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Notes */}
            {delivery.notes && (
              <div className="print-keep-together">
                <h3 className="font-bold mb-2">Delivery Notes:</h3>
                <p className="text-sm bg-gray-50 p-4 rounded">{delivery.notes}</p>
              </div>
            )}

            {/* Signatures */}
            <div className="mt-12 pt-8 border-t-2 border-gray-800 signature-section">
              <h3 className="font-bold text-lg mb-6">ACKNOWLEDGMENT OF RECEIPT</h3>
              <p className="text-sm mb-8">
                I acknowledge receipt of the above listed equipment in good condition and confirm that all serial numbers have been verified.
              </p>
              
              <div className="grid grid-cols-2 gap-8 mt-8">
                <div>
                  <div className="border-b-2 border-gray-800 mb-2 h-16">
                    {delivery.received_by && (
                      <div className="pt-12 text-center font-semibold">{delivery.received_by}</div>
                    )}
                  </div>
                  <div className="text-sm font-semibold">Received By (Print Name)</div>
                </div>
                <div>
                  <div className="border-b-2 border-gray-800 mb-2 h-16"></div>
                  <div className="text-sm font-semibold">Date</div>
                </div>
              </div>
              
              <div className="mt-8">
                <div className="border-b-2 border-gray-800 mb-2 h-16 w-2/3"></div>
                <div className="text-sm font-semibold">Signature</div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

